/**
 * Action ID constants - exported for convenience
 * Use these when configuring menu items: { id: PopoverMenu.ResetPassword }
 */
export const ACTION_IDS = {
  ResetPassword: 'reset-password',
  BlockSignIn: 'block-signin',
  UnblockSignIn: 'unblock-signin',
  SuspendSignIn: 'suspend-signin',
  UnsuspendSignIn: 'unsuspend-signin',
  RevokeSessions: 'revoke-sessions',
  ManageGroups: 'manage-groups',
  ManageSecurityGroups: 'manage-security-groups',
  ManageSharedMailboxes: 'manage-shared-mailboxes',
  ManageDistributionMailboxes: 'manage-distribution-mailboxes',
  ManageLicenses: 'manage-licenses',
  OffboardUser: 'offboard-user',
  ReRegisterMfa: 're-register-mfa',
  TemporaryAccessPass: 'temporary-access-pass',
  OutOfOffice: 'out-of-office',
  GlobalAddressList: 'global-address-list',
  ManageEmailForwarding: 'manage-email-forwarding',
  ManageMailboxPermissions: 'manage-mailbox-permissions',
  EditUserProperties: 'edit-user-properties',
}

/**
 * Action ID to Integration Action constant mapping
 * These constants match the values exported from m365/actions.js and gws/actions.js
 * Using string literals directly to avoid circular dependencies
 */
const M365_ACTION_MAP = {
  'manage-groups': 'M365_MANAGE_GROUPS',
  'manage-security-groups': 'M365_MANAGE_SECURITY_GROUPS',
  'manage-shared-mailboxes': 'M365_MANAGE_SHARED_GROUPS',
  'manage-distribution-mailboxes': 'M365_MANAGE_DISTRIBUTION_GROUPS',
  're-register-mfa': 'M365_RE_REGISTER_MFA',
  'temporary-access-pass': 'M365_TEMPORARY_ACCESS_PASS',
  'out-of-office': 'M365_OUT_OF_OFFICE',
  'global-address-list': 'M365_GLOBAL_ADDRESS_LIST',
  'manage-email-forwarding': 'M365_MANAGE_EMAIL_FORWARDING',
}

const GWS_ACTION_MAP = {
  'manage-groups': 'GWS_MANAGE_GROUPS',
  'manage-licenses': 'GWS_MANAGE_LICENSE',
  'revoke-sessions': 'GWS_REVOKE_SESSIONS',
  'reset-password': 'GWS_PWD_RESET',
  'offboard-user': 'GWS_DELETE_USER',
  'suspend-signin': 'GWS_DISABLE_USER',
  'unsuspend-signin': 'GWS_ENABLE_USER',
}

/**
 * Get M365 action constant for a given action ID
 * @param {string} actionId - The action ID (e.g., 'manage-groups')
 * @returns {string|null} The M365 action constant or null if not found
 */
const getM365Action = (actionId) => M365_ACTION_MAP[actionId] || null

/**
 * Get GWS action constant for a given action ID
 * @param {string} actionId - The action ID (e.g., 'manage-groups')
 * @returns {string|null} The GWS action constant or null if not found
 */
const getGWSAction = (actionId) => GWS_ACTION_MAP[actionId] || null

/**
 * List of the configuration for what is displayed on a row button.
 */

export const ACTION_DEFINITIONS = {
  'reset-password': {
    label: 'ENGAGE.RESET_PASSWORD',
    icon: 'fa-key',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'block-signin': {
    label: 'ENGAGE.BLOCK_SIGNIN',
    icon: 'fa-ban',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultShouldRender: (employee) => employee?.enabled === true,
  },
  'unblock-signin': {
    label: 'ENGAGE.UNBLOCK_SIGNIN',
    icon: 'fa-ban',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultShouldRender: (employee) => employee?.enabled === false,
  },
  'suspend-signin': {
    label: 'ENGAGE.SUSPEND_SIGN_IN',
    icon: 'fa-ban',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultShouldRender: (employee) => employee?.enabled === true,
  },
  'unsuspend-signin': {
    label: 'ENGAGE.UNSUSPEND_SIGN_IN',
    icon: 'fa-ban',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultShouldRender: (employee) => employee?.enabled === false,
  },
  'revoke-sessions': {
    label: 'ENGAGE.SIGN_OUT_ALL_APPS',
    icon: 'fa-right-from-bracket',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'manage-groups': {
    label: 'ENGAGE.MANAGE_GROUPS',
    icon: 'fas fa-users-gear',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    getLabel: (context) =>
      context.integrationType === 'Office365' ? 'ENGAGE.MANAGE_M365_GROUPS' : 'ENGAGE.MANAGE_GROUPS',
  },
  'manage-security-groups': {
    label: 'ENGAGE.MANAGE_SECURITY_GROUPS',
    icon: 'fas fa-users-gear',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'manage-shared-mailboxes': {
    label: 'ENGAGE.MANAGE_SHARED_MAILBOXES',
    icon: 'fas fa-users-gear',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'manage-distribution-mailboxes': {
    label: 'ENGAGE.MANAGE_DISTRIBUTION_GROUPS',
    icon: 'fas fa-users-gear',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'manage-licenses': {
    label: 'ENGAGE.MANAGE_LICENSES',
    icon: 'fa-user-cog',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'offboard-user': {
    label: 'ENGAGE.OFFBOARD_USER_TITLE',
    icon: 'fa-user-minus',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  're-register-mfa': {
    label: 'ENGAGE.RE_REGISTER_MFA',
    icon: 'fa-user-shield',
    defaultPermissionCheck: (context) =>
      context.isAuthorized && (context.scopePermissions?.['re-register-mfa'] ?? true),
  },
  'temporary-access-pass': {
    label: 'ENGAGE.TEMPORARY_ACCESS_PASS_CREATE',
    icon: 'fa-unlock-alt',
    defaultPermissionCheck: (context) =>
      context.isAuthorized && (context.scopePermissions?.['temporary-access-pass'] ?? true),
  },
  'out-of-office': {
    label: 'ENGAGE.OUT_OF_OFFICE',
    icon: 'fa-plane',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
  'global-address-list': {
    label: 'ENGAGE.GLOBAL_ADDRESS_LIST',
    icon: 'fa-address-book',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultAvailabilityCheck: (employee) => ({
      isAvailable: employee?.isUserMailBoxAvailable || employee?.isUserSharedMailbox || false,
    }),
  },
  'manage-email-forwarding': {
    label: 'ENGAGE.MANAGE_EMAIL_FORWARDING',
    icon: 'fa-envelope',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultAvailabilityCheck: (employee) => ({
      isAvailable: employee?.isUserMailBoxAvailable || employee?.isUserSharedMailbox || false,
    }),
  },
  'manage-mailbox-permissions': {
    label: 'ENGAGE.MAILBOX_DELEGATIONS.TITLE',
    icon: 'fa-envelope-open',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
    defaultAvailabilityCheck: (employee) => ({
      isAvailable: employee?.isUserMailBoxAvailable === true,
    }),
  },
  'edit-user-properties': {
    label: 'ENGAGE.EDIT_USER_PROPERTIES',
    icon: 'fa-pen-to-square',
    defaultPermissionCheck: (context) => context.isAuthorized ?? false,
  },
}

/**
 * Action to IntegrationModal action mapping (for ViewEmployeeDataModal pattern)
 */
const ACTION_TO_INTEGRATION_MODAL = {
  'reset-password': 'RESET_PASSWORD',
  'block-signin': 'BLOCK_SIGNIN',
  'unblock-signin': 'UNBLOCK_SIGNIN',
  'revoke-sessions': 'SIGNOUT_OF_ALL_APPS',
}

/**
 * Action to MODAL_NAMES mapping (for EngageTableSection pattern)
 * These match the MODAL_NAMES constants from engage/utils.js
 */
const ACTION_TO_MODAL_NAME = {
  'reset-password': 'RESET_PASSWORD',
  'block-signin': 'BLOCK_SIGNIN',
  'unblock-signin': 'UNBLOCK_SIGNIN',
  'revoke-sessions': 'REVOKE_SESSIONS',
  're-register-mfa': 'RE_REGISTER_MFA',
  'temporary-access-pass': 'TEMPORARY_ACCESS_PASS',
  'out-of-office': 'OUT_OF_OFFICE',
  'global-address-list': 'GLOBAL_ADDRESS_LIST',
  'manage-email-forwarding': 'MANAGE_EMAIL_FORWARDING',
  'manage-mailbox-permissions': 'MAILBOX_PERMISSIONS',
  'manage-licenses': 'MANAGE_LICENSES',
  'offboard-user': 'OFFBOARD_USER',
  'edit-user-properties': 'EDIT_EMPLOYEE_DATA',
}

/**
 * Helper to get integration app name from context
 */
const getIntegrationAppName = (context) =>
  context.selectedIntegration?.IntegrationApplication?.application_name || context.integrationType

/**
 * Helper to check if action requires array of employees
 */
const requiresArrayEmployee = (actionId) =>
  [
    're-register-mfa',
    'temporary-access-pass',
    'out-of-office',
    'global-address-list',
    'manage-email-forwarding',
  ].includes(actionId)

/**
 * Action handlers each button predefined.
 * Pass along any properties in the context.
 */
export const DEFAULT_ACTION_HANDLERS = {
  'reset-password': (employee, context) => {
    if (!context.isAuthorized) return

    // Priority 1: IntegrationModals pattern
    if (context.openIntegrationModal && ACTION_TO_INTEGRATION_MODAL['reset-password']) {
      context.openIntegrationModal(ACTION_TO_INTEGRATION_MODAL['reset-password'])
      return
    }
    // Priority 2: EngageTableSection pattern
    if (context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365' && ACTION_TO_MODAL_NAME['reset-password']) {
        context.setModal({
          selectedEmployee: employee,
          openedModalName: ACTION_TO_MODAL_NAME['reset-password'],
        })
      } else if (appName === 'GWS' && context.setAction) {
        const action = getGWSAction('reset-password')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    }
  },
  'block-signin': (employee, context) => {
    if (context.openIntegrationModal && ACTION_TO_INTEGRATION_MODAL['block-signin']) {
      context.openIntegrationModal(ACTION_TO_INTEGRATION_MODAL['block-signin'])
      return
    }
    if (context.setModal && ACTION_TO_MODAL_NAME['block-signin']) {
      context.setModal({
        selectedEmployee: employee,
        openedModalName: ACTION_TO_MODAL_NAME['block-signin'],
      })
      return
    }
    if (context.onBlockSignInClick) {
      context.onBlockSignInClick(employee)
    }
  },
  'unblock-signin': (employee, context) => {
    if (context.openIntegrationModal && ACTION_TO_INTEGRATION_MODAL['unblock-signin']) {
      context.openIntegrationModal(ACTION_TO_INTEGRATION_MODAL['unblock-signin'])
      return
    }
    if (context.setModal && ACTION_TO_MODAL_NAME['unblock-signin']) {
      context.setModal({
        selectedEmployee: employee,
        openedModalName: ACTION_TO_MODAL_NAME['unblock-signin'],
      })
      return
    }
    if (context.onUnblockSignInClick) {
      context.onUnblockSignInClick(employee)
    }
  },
  'suspend-signin': (employee, context) => {
    if (!context.isAuthorized || !context.setAction || !context.setModal) return

    const appName = getIntegrationAppName(context)
    if (appName === 'GWS') {
      const action = getGWSAction('suspend-signin')
      if (action) {
        context.setAction(action)
        context.setModal({
          selectedEmployee: employee,
          openedModalName: 'INTEGRATION_MANAGER',
        })
      }
    }
  },
  'unsuspend-signin': (employee, context) => {
    if (!context.isAuthorized || !context.setAction || !context.setModal) return

    const appName = getIntegrationAppName(context)
    if (appName === 'GWS') {
      const action = getGWSAction('unsuspend-signin')
      if (action) {
        context.setAction(action)
        context.setModal({
          selectedEmployee: employee,
          openedModalName: 'INTEGRATION_MANAGER',
        })
      }
    }
  },
  'revoke-sessions': (employee, context) => {
    if (!context.isAuthorized) return

    // Priority 1: IntegrationModals pattern
    if (context.openIntegrationModal && ACTION_TO_INTEGRATION_MODAL['revoke-sessions']) {
      context.openIntegrationModal(ACTION_TO_INTEGRATION_MODAL['revoke-sessions'])
      return
    }
    // Priority 2: EngageTableSection pattern
    if (context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365' && ACTION_TO_MODAL_NAME['revoke-sessions']) {
        context.setModal({
          selectedEmployee: employee,
          openedModalName: ACTION_TO_MODAL_NAME['revoke-sessions'],
        })
      } else if (appName === 'GWS' && context.setAction) {
        const action = getGWSAction('revoke-sessions')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    }
  },
  'manage-groups': (employee, context) => {
    if (!context.isAuthorized) return

    // Check if we have setAction and setModal (EngageTableSection pattern)
    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365') {
        const action = getM365Action('manage-groups')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      } else if (appName === 'GWS') {
        const action = getGWSAction('manage-groups')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    } else {
      // ViewEmployeeDataModal pattern - these actions aren't supported via IntegrationModals
      console.warn(
        'manage-groups: This action requires IntegrationManager modal which is not available in this context',
      )
    }
  },
  'manage-security-groups': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365') {
        const action = getM365Action('manage-security-groups')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    } else {
      console.warn(
        'manage-security-groups: This action requires IntegrationManager modal which is not available in this context',
      )
    }
  },
  'manage-shared-mailboxes': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365') {
        const action = getM365Action('manage-shared-mailboxes')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    } else {
      console.warn(
        'manage-shared-mailboxes: This action requires IntegrationManager modal which is not available in this context',
      )
    }
  },
  'manage-distribution-mailboxes': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365') {
        const action = getM365Action('manage-distribution-mailboxes')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    } else {
      console.warn(
        'manage-distribution-mailboxes: This action requires IntegrationManager modal which is not available in this context',
      )
    }
  },
  'manage-licenses': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365' && ACTION_TO_MODAL_NAME['manage-licenses']) {
        context.setModal({
          selectedEmployee: employee,
          openedModalName: ACTION_TO_MODAL_NAME['manage-licenses'],
        })
      } else if (appName === 'GWS' && context.setAction) {
        const action = getGWSAction('manage-licenses')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    } else {
      console.warn('manage-licenses: This action requires setModal which is not available in this context')
    }
  },
  'offboard-user': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setModal) {
      const appName = getIntegrationAppName(context)
      if (appName === 'Office365' && ACTION_TO_MODAL_NAME['offboard-user']) {
        context.setModal({
          selectedEmployee: employee,
          openedModalName: ACTION_TO_MODAL_NAME['offboard-user'],
        })
      } else if (appName === 'GWS' && context.setAction) {
        const action = getGWSAction('offboard-user')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee: employee,
            openedModalName: 'INTEGRATION_MANAGER',
          })
        }
      }
    } else {
      console.warn('offboard-user: This action requires setModal which is not available in this context')
    }
  },
  're-register-mfa': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      const selectedEmployee = requiresArrayEmployee('re-register-mfa') ? [employee] : employee

      if (appName === 'Office365') {
        const action = getM365Action('re-register-mfa')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee,
            openedModalName: ACTION_TO_MODAL_NAME['re-register-mfa'],
          })
        }
      }
    } else {
      console.warn(
        're-register-mfa: This action requires setAction and setModal which are not available in this context',
      )
    }
  },
  'temporary-access-pass': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      const selectedEmployee = requiresArrayEmployee('temporary-access-pass') ? [employee] : employee

      if (appName === 'Office365') {
        const action = getM365Action('temporary-access-pass')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee,
            openedModalName: ACTION_TO_MODAL_NAME['temporary-access-pass'],
          })
        }
      }
    } else {
      console.warn(
        'temporary-access-pass: This action requires setAction and setModal which are not available in this context',
      )
    }
  },
  'out-of-office': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      const selectedEmployee = requiresArrayEmployee('out-of-office') ? [employee] : employee

      if (appName === 'Office365') {
        const action = getM365Action('out-of-office')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee,
            openedModalName: ACTION_TO_MODAL_NAME['out-of-office'],
          })
        }
      }
    } else {
      console.warn('out-of-office: This action requires setAction and setModal which are not available in this context')
    }
  },
  'global-address-list': (employee, context) => {
    // Check availability before calling
    const availability = context.actionConditions?.['global-address-list']?.(employee)
    if (!availability?.isAvailable || !context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      const selectedEmployee = requiresArrayEmployee('global-address-list') ? [employee] : employee

      if (appName === 'Office365') {
        const action = getM365Action('global-address-list')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee,
            openedModalName: ACTION_TO_MODAL_NAME['global-address-list'],
          })
        }
      }
    } else {
      console.warn(
        'global-address-list: This action requires setAction and setModal which are not available in this context',
      )
    }
  },
  'manage-email-forwarding': (employee, context) => {
    // Check availability before calling
    const availability = context.actionConditions?.['manage-email-forwarding']?.(employee)
    if (!availability?.isAvailable || !context.isAuthorized) return

    if (context.setAction && context.setModal) {
      const appName = getIntegrationAppName(context)
      const selectedEmployee = requiresArrayEmployee('manage-email-forwarding') ? [employee] : employee

      if (appName === 'Office365') {
        const action = getM365Action('manage-email-forwarding')
        if (action) {
          context.setAction(action)
          context.setModal({
            selectedEmployee,
            openedModalName: ACTION_TO_MODAL_NAME['manage-email-forwarding'],
          })
        }
      }
    } else {
      console.warn(
        'manage-email-forwarding: This action requires setAction and setModal which are not available in this context',
      )
    }
  },
  'manage-mailbox-permissions': (employee, context) => {
    if (employee?.isUserMailBoxAvailable !== true || !context.isAuthorized || !context.setModal) {
      return
    }
    if (getIntegrationAppName(context) !== 'Office365') {
      return
    }
    context.setModal({
      selectedEmployee: employee,
      openedModalName: ACTION_TO_MODAL_NAME['manage-mailbox-permissions'],
    })
  },
  'edit-user-properties': (employee, context) => {
    if (!context.isAuthorized) return

    if (context.setModal && ACTION_TO_MODAL_NAME['edit-user-properties']) {
      context.setModal({
        selectedEmployee: employee,
        openedModalName: ACTION_TO_MODAL_NAME['edit-user-properties'],
      })
    }
  },
}

/**
 * Default disabled popover content
 */
export const DEFAULT_DISABLED_POPOVER = {
  title: 'ENGAGE.INSUFFICIENT_PERMISSIONS',
  actionButton: {
    label: 'INTEGRATIONS.GO_TO',
  },
}

/**
 * Special popover for Exchange Online not connected
 */
export const EXO_NOT_CONNECTED_POPOVER = {
  title: 'ENGAGE.EXCHANGE_ONLINE_NOT_CONNECTED',
  actionButton: {
    label: 'INTEGRATION.CONNECT_EXCHANGE_ONLINE',
  },
}
